import React, { useState, useRef, useEffect } from 'react';
import { getAiDisclaimerAccepted, setAiDisclaimerAccepted } from '@shared/services/userContentService';
import { Icons } from '@shared/constants';
import type { ChatSession } from '@shared/types';
import { useAuth } from '@/context/AuthContext';
import { useStreamingChat } from '@/hooks/useStreamingChat';
import { useChatFreeTierGate } from '@/hooks/useChatFreeTierGate';
import { usePaywall } from '@/hooks/usePaywall';
import { ChatHeader, renderMarkdown, MAX_MESSAGE_LENGTH } from './chatShared';
import { applyChatPrivacyLexiconStreamingSafe } from '@shared/utils/chatPrivacyTokens';

interface ChatCoachProps {
  onClose?: () => void;
}

export const ChatCoach: React.FC<ChatCoachProps> = ({ onClose }) => {
  const { currentUser } = useAuth();
  const [disclaimerState, setDisclaimerState] = useState<'loading' | 'pending' | 'accepted'>('loading');
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    getAiDisclaimerAccepted(currentUser.uid).then((accepted) => {
      setDisclaimerState(accepted ? 'accepted' : 'pending');
    });
  }, [currentUser]);

  const handleAccept = async () => {
    if (!currentUser) return;
    await setAiDisclaimerAccepted(currentUser.uid);
    setDisclaimerState('accepted');
  };

  const handleDecline = () => {
    onClose?.();
  };

  if (disclaimerState === 'loading') {
    return (
      <div className="flex flex-col h-full bg-white overflow-hidden">
        <ChatHeader onClose={onClose} />
        <div className="flex-1 flex items-center justify-center">
          <span className="text-sm text-growth/50">Loading...</span>
        </div>
      </div>
    );
  }

  if (disclaimerState === 'pending') {
    return (
      <div className="flex flex-col h-full bg-white overflow-hidden">
        <ChatHeader onClose={onClose} />
        <div className="flex-1 overflow-y-auto">
          <div className="min-h-full flex items-center justify-center p-6">
            <div className="max-w-sm text-center space-y-5">

            <p className="text-sm text-growth leading-relaxed">
              The wellness assistant is powered by Google's Gemini AI (via Google Cloud) and may produce inaccurate information. It's a quick way to explore your financial data — not a substitute for professional financial advice.
            </p>
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="text-xs text-growth/70 underline hover:text-growth transition-colors"
            >
              {showDetails ? 'Less info' : 'More info'}
            </button>
            {showDetails && (
              <p className="text-xs text-growth/70 leading-relaxed">
                To answer your questions, your messages and a summary of your financial data are sent to Google's Gemini AI during your conversation. Your conversations are stored securely in your account. Members can reopen a recent chat from the last 30 days — and they are only sent to Google's Gemini AI while you are actively in the conversation. They are not used to train AI models. See our{' '}
                <a href="/legal" className="underline hover:text-growth transition-colors">privacy policy</a> for details.
              </p>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleDecline}
                className="px-5 py-2.5 rounded-full text-sm font-medium text-growth bg-sand hover:bg-mist/30 transition-colors"
              >
                Not now
              </button>
              <button
                onClick={handleAccept}
                className="px-5 py-2.5 rounded-full text-sm font-medium text-white bg-growth hover:bg-growth/90 transition-colors shadow-md"
              >
                I understand
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  return <ChatView onClose={onClose} />;
};

function formatSessionDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ChatView({ onClose }: { onClose?: () => void }) {
  const { currentUser } = useAuth();
  // Deliberately any-paid (isDataConnect): chat resume is a membership
  // perk granted to every paid level server-side.
  const { isDataConnect, isReady } = usePaywall();
  const canResume = isReady ? isDataConnect : undefined;
  const {
    messages,
    isStreaming,
    isLoadingHistory,
    remainingMessages,
    sendMessage,
    privacyLexicon,
    recentSessions,
    loadRecentSessions,
    resumeSession,
    startNewChat,
  } = useStreamingChat({ uid: currentUser?.uid, canResume });
  const { tryConsumeMessage, refundMessage, freeMessagesRemaining } = useChatFreeTierGate();
  const [input, setInput] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  // True while an IME / voice dictation / predictive-text composition is open.
  const isComposingRef = useRef(false);

  useEffect(() => {
    if (!showHistory) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, showHistory]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming || remainingMessages === 0) return;
    if (!tryConsumeMessage()) return; // free daily limit reached — paywall shown
    void sendMessage(trimmed).then(delivered => {
      if (!delivered) refundMessage(); // failed send shouldn't burn a free message
    });
    setInput('');
  };

  // Enter must never submit a composition-in-progress. iOS voice dictation,
  // predictive-text acceptance and IME candidate commits all deliver a keydown
  // with key === 'Enter' while the text is still being composed, which fired
  // handleSend mid-sentence and shipped truncated fragments ("I'm just not").
  // Three overlapping signals because no single one is universal: the standard
  // `isComposing` flag, the legacy keyCode 229 IME sentinel, and our own
  // composition-event bookkeeping for engines that report neither.
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    if (isComposingRef.current || e.nativeEvent.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    handleSend();
  };

  const handleOpenHistory = () => {
    setShowHistory(true);
    void loadRecentSessions();
  };

  const handleResume = (session: ChatSession) => {
    setShowHistory(false);
    void resumeSession(session);
  };

  const hasConversation = messages.length > 1;

  const headerActions = canResume ? (
    <>
      {hasConversation && (
        <button
          onClick={startNewChat}
          aria-label="Start a new chat"
          title="New chat"
          className="p-2 text-growth bg-white/50 rounded-full hover:bg-white transition-all"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
        </button>
      )}
      <button
        onClick={showHistory ? () => setShowHistory(false) : handleOpenHistory}
        aria-label="Recent chats"
        title="Recent chats"
        className={`p-2 rounded-full transition-all ${showHistory ? 'bg-growth text-white' : 'text-growth bg-white/50 hover:bg-white'}`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
      </button>
    </>
  ) : undefined;

  const charsRemaining = MAX_MESSAGE_LENGTH - input.length;
  const showCharCount = charsRemaining <= 100;

  return (
    <div className="flex flex-col bg-white overflow-hidden h-full">
      <ChatHeader onClose={onClose} actions={headerActions} />

      {showHistory ? (
        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-white/50">
          <p className="text-xs text-growth/70 px-1 pb-1">Pick up a conversation from the last 30 days.</p>
          {recentSessions.length === 0 ? (
            <p className="text-sm text-growth/50 px-1 py-6 text-center">No recent chats yet.</p>
          ) : (
            recentSessions.map((session) => (
              <button
                key={session.id}
                onClick={() => handleResume(session)}
                className="w-full text-left px-4 py-3 rounded-2xl bg-sand border border-mist/20 hover:border-mist/50 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-growth/70">{formatSessionDate(session.lastMessageAt)}</span>
                  <span className="text-xs text-growth/50">{session.messageCount} messages</span>
                </div>
                <p className="ph-mask text-sm text-growth truncate mt-0.5">
                  {session.lastUserMessage || 'Conversation'}
                </p>
              </button>
            ))
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white/50">
          {isLoadingHistory && (
            <p className="text-xs text-growth/50 text-center">Loading conversation...</p>
          )}
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`ph-mask max-w-[85%] px-5 py-3 rounded-2xl text-sm leading-relaxed shadow-xs ${
                  msg.role === 'user'
                    ? 'bg-growth text-white rounded-tr-none'
                    : 'bg-sand text-growth-light rounded-tl-none border border-mist/20'
                }`}
              >
                {msg.text ? (msg.role === 'model' ? renderMarkdown(applyChatPrivacyLexiconStreamingSafe(msg.text, privacyLexicon), onClose) : msg.text) : (
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-growth/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-growth/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-growth/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                )}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      )}

      <div className="p-4 bg-white border-t border-mist/10" style={{ paddingBottom: 'calc(var(--sab) + 1rem)' }}>
        {freeMessagesRemaining !== undefined && freeMessagesRemaining <= 3 ? (
          <p className="text-xs text-growth/70 text-center mb-2">
            {freeMessagesRemaining === 0
              ? 'Free daily limit reached — membership unlocks unlimited chat'
              : `${freeMessagesRemaining} free ${freeMessagesRemaining === 1 ? 'message' : 'messages'} left today`}
          </p>
        ) : remainingMessages !== undefined && remainingMessages <= 10 ? (
          <p className="text-xs text-growth/50 text-center mb-2">
            {remainingMessages === 0 ? 'Daily limit reached' : `${remainingMessages} messages remaining today`}
          </p>
        ) : null}
        <div className="relative flex items-center">
          <input
            type="text"
            value={input}
            onChange={(e) => {
              if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
                setInput(e.target.value);
              }
            }}
            onKeyDown={handleInputKeyDown}
            onCompositionStart={() => { isComposingRef.current = true; }}
            onCompositionEnd={() => { isComposingRef.current = false; }}
            // Safety valve: if an engine ever drops compositionend (e.g. the field
            // loses focus mid-composition) a stuck flag would silently disable Enter
            // forever. Losing focus always ends composition, so clear it here too.
            onBlur={() => { isComposingRef.current = false; }}
            onFocus={() => setShowHistory(false)}
            placeholder="Ask about your spending, debt, subscriptions..."
            className="w-full bg-sand/50 rounded-full pl-5 pr-12 py-3 text-sm text-growth focus:outline-hidden focus:ring-2 focus:ring-foliage/20 transition-all"
          />
          <button
            onClick={handleSend}
            aria-label="Send message"
            disabled={!input.trim() || isStreaming || remainingMessages === 0}
            className="absolute right-2 p-2 bg-growth text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-growth/90 transition-colors shadow-md"
          >
            <Icons.Activity className="w-4 h-4" />
          </button>
        </div>
        {showCharCount && (
          <p className={`text-xs text-right mt-1 ${charsRemaining <= 20 ? 'text-wildfire' : 'text-growth/50'}`}>
            {charsRemaining} characters remaining
          </p>
        )}
      </div>
    </div>
  );
}
